/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 */

import { useEffect, useState } from "react";
import { StoreProduct, StoreVariant } from "@/lib/store-api";
import { ProductImage } from "@/components/product-image";
import { ImageLightbox } from "@/components/image-lightbox";

const FALLBACK_IMG = "https://placehold.co/400x400/1a1a1a/666?text=No+Image";

interface Props {
  product: StoreProduct;
  variant: StoreVariant;
  alt: string;
}

/**
 * Product detail page image slot: main image (plain zoom-on-hover, never
 * swaps to a worn shot — see ProductImage's `hoverZoom` prop), a thumbnail
 * strip for every image of the currently selected variant/color, and a
 * lightbox opened by clicking the main image.
 */
export function ProductGallery({ product, variant, alt }: Props) {
  const cover = variant?.image_url || product.image_url || FALLBACK_IMG;
  const images = [{ url: cover, kind: "cover" as const }, ...(variant?.images ?? [])];

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const safeIndex = Math.min(selectedIndex, images.length - 1);

  useEffect(() => {
    setSelectedIndex(0);
  }, [variant?.sku]);

  return (
    <div>
      <ProductImage
        src={images[safeIndex].url}
        alt={alt}
        hoverZoom
        variantCount={product.variants.length}
        onClick={() => setLightboxOpen(true)}
      />

      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((img, i) => (
            <button
              key={`${img.url}-${i}`}
              type="button"
              onClick={() => setSelectedIndex(i)}
              className={`shrink-0 w-16 h-16 bg-white rounded-lg overflow-hidden border-2 transition-colors ${
                i === safeIndex ? "border-primary" : "border-transparent"
              }`}
            >
              <img
                src={img.url}
                alt={`${alt} ${img.kind} ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = FALLBACK_IMG;
                }}
              />
            </button>
          ))}
        </div>
      )}

      <ImageLightbox
        images={images}
        initialIndex={safeIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}
